

class RobustLazyLoader {
  constructor(options = {}) {
    this.options = {
      root: options.root || null,
      rootMargin: options.rootMargin || '50px',
      threshold: options.threshold || 0.01,
      maxRetries: options.maxRetries || 5,
      retryDelay: options.retryDelay || 1000,
      forceLoad: options.forceLoad !== false,
    };
    this.loadedImages = new WeakSet();
    this.retryMap = new Map();
    this.observer = null;
    this.init();
  }

  init() {
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        root: this.options.root,
        rootMargin: this.options.rootMargin,
        threshold: this.options.threshold
      }
    );
    this.observeImages();
    this.startMutationObserver();
    this.startPeriodicCheck();
    this.setupEventListeners();
  }

  observeImages() {
    const images = document.querySelectorAll('img[data-src], img[data-srcset]');
    images.forEach(img => {
      if (img.hasAttribute('data-src') || img.hasAttribute('data-srcset')) {
        this.observer.observe(img);
        if (this.isInViewport(img)) {
          this.loadImage(img);
        }
      }
    });
  }

  handleIntersection(entries, observer) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        this.loadImage(img);
        observer.unobserve(img);
      }
    });
  }

  async loadImage(img) {
    if (this.loadedImages.has(img)) return;
    if (!this.validateImageElement(img)) return;
    const dataSrc = img.getAttribute('data-src');
    const dataSrcset = img.getAttribute('data-srcset');
    const dataSizes = img.getAttribute('data-sizes');
    if (!dataSrc && !dataSrcset) return;
    try {
      img.loading = 'eager';
      await this.attemptLoad(img, dataSrc, dataSrcset, dataSizes);
      this.loadedImages.add(img);
      this.retryMap.delete(img);
      img.dispatchEvent(new CustomEvent('lazyloaded', { detail: { src: dataSrc, srcset: dataSrcset } }));
    } catch (error) {
      this.handleLoadError(img, dataSrc, dataSrcset, dataSizes);
    }
  }

  async attemptLoad(img, dataSrc, dataSrcset, dataSizes, attempt = 0) {
    return new Promise((resolve, reject) => {
      const maxAttempts = this.options.maxRetries;
      const onLoad = () => {
        cleanup();
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-sizes');
        img.classList.add('lazy-loaded');
        resolve();
      };
      const onError = () => {
        cleanup();
        if (attempt < maxAttempts) {
          const delay = this.options.retryDelay * Math.pow(2, attempt);
          setTimeout(() => {
            this.attemptLoad(img, dataSrc, dataSrcset, dataSizes, attempt + 1).then(resolve).catch(reject);
          }, delay);
        } else {
          reject(new Error('Max retries reached'));
        }
      };
      const cleanup = () => {
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
      };
      img.addEventListener('load', onLoad);
      img.addEventListener('error', onError);
      this.setSources(img, dataSrc, dataSrcset, dataSizes);
      if (this.options.forceLoad) {
        setTimeout(() => {
          if (!this.loadedImages.has(img)) {
            cleanup();
            resolve();
          }
        }, this.options.retryDelay * maxAttempts * 3);
      }
    });
  }

  setSources(img, dataSrc, dataSrcset, dataSizes) {
    if (dataSrcset && this.validateUrl(dataSrcset)) img.srcset = dataSrcset;
    if (dataSizes) img.sizes = dataSizes;
    if (dataSrc && this.validateUrl(dataSrc)) img.src = dataSrc;
    if (img.complete && img.naturalWidth === 0) {
      const currentSrc = img.src;
      img.src = '';
      img.src = currentSrc;
    }
  }

  validateImageElement(img) {
    return (
      img instanceof HTMLImageElement &&
      img.isConnected &&
      !img.hasAttribute('data-lazy-loading')
    );
  }

  validateUrl(url) {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (trimmed.length === 0) return false;
    return (
      trimmed.startsWith('data:') ||
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('//') ||
      trimmed.startsWith('/') ||
      trimmed.startsWith('./')
    );
  }

  handleLoadError(img, dataSrc, dataSrcset, dataSizes) {
    img.classList.add('lazy-error');
    const fallback = img.getAttribute('data-fallback');
    if (fallback && this.validateUrl(fallback)) img.src = fallback;
    img.dispatchEvent(new CustomEvent('lazyerror', { detail: { src: dataSrc, srcset: dataSrcset } }));
  }

  startPeriodicCheck() {
    setInterval(() => {
      this.observeImages();
      this.forceLoadVisible();
    }, 500);
  }

  startMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldCheck = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              if (node.tagName === 'IMG' && (node.hasAttribute('data-src') || node.hasAttribute('data-srcset'))) {
                this.loadImage(node);
                shouldCheck = true;
              } else if (node.querySelectorAll) {
                const imgs = node.querySelectorAll('img[data-src], img[data-srcset]');
                if (imgs.length > 0) shouldCheck = true;
              }
            }
          });
        }
        if (mutation.type === 'attributes' && mutation.target.tagName === 'IMG') {
          const img = mutation.target;
          const attrName = mutation.attributeName;
          if (attrName === 'data-src' || attrName === 'data-srcset') {
            this.loadedImages.delete(img);
            if (this.isInViewport(img)) {
              this.loadImage(img);
            } else {
              this.observer.observe(img);
            }
            shouldCheck = true;
          }
        }
      });
      if (shouldCheck) {
        this.observeImages();
      }
    });
    this.mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-src', 'data-srcset', 'src', 'srcset']
    });
  }

  setupEventListeners() {
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.forceLoadVisible();
      }, 150);
    }, { passive: true });
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.forceLoadVisible();
      }, 300);
    });
    window.addEventListener('pageshow', () => {
      this.forceLoadVisible();
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.forceLoadVisible();
      }
    });
  }

  forceLoadVisible() {
    const images = document.querySelectorAll('img[data-src], img[data-srcset]');
    images.forEach(img => {
      if (this.isInViewport(img) && !this.loadedImages.has(img)) {
        this.loadImage(img);
      }
    });
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    return (
      rect.top >= -rect.height &&
      rect.left >= -rect.width &&
      rect.bottom <= windowHeight + rect.height &&
      rect.right <= windowWidth + rect.width
    );
  }

  loadAll() {
    const images = document.querySelectorAll('img[data-src], img[data-srcset]');
    images.forEach(img => this.loadImage(img));
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  reloadImage(img) {
    if (img instanceof HTMLImageElement) {
      this.loadedImages.delete(img);
      this.loadImage(img);
    }
  }
}

const lazyLoader = new RobustLazyLoader({
  rootMargin: '100px',
  threshold: 0.01,
  maxRetries: 5,
  retryDelay: 1000,
  forceLoad: true
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = RobustLazyLoader;
}

window.RobustLazyLoader = RobustLazyLoader;
window.lazyLoader = lazyLoader;
